import { memo } from 'react';
import { Box, CircularProgress, Skeleton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { K } from '../apiServiceI18n';

const CHART_SKELETON_HEIGHTS = [72, 48, 92, 36, 64, 55, 80];

function ApiServicePerformanceLoader({
  message,
  variant = 'chart',
  minHeight = 280,
  compact = false,
}) {
  const { t } = useTranslation();
  const label = message ?? t(K.API_SVC_PERF_LOADING);

  if (variant === 'inline') {
    return (
      <Box
        role="status"
        aria-live="polite"
        aria-busy="true"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          minHeight,
          py: compact ? 3 : 6,
        }}
      >
        <CircularProgress size={compact ? 28 : 36} aria-hidden="true" />
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      role="status"
      aria-live="polite"
      aria-busy="true"
      sx={{
        minHeight,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <CircularProgress size={22} aria-hidden="true" />
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </Box>

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'flex-end',
          gap: 2.5,
          px: 6,
          pb: 5,
          pt: 1,
          borderBottom: '1px solid #e8eaef',
        }}
      >
        {CHART_SKELETON_HEIGHTS.map((height, index) => (
          <Skeleton
            key={index}
            variant="rounded"
            width={56}
            height={height}
            animation="wave"
            sx={{ borderRadius: 1, bgcolor: '#e8eaef', flexShrink: 0 }}
          />
        ))}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, px: 6, pb: 1 }}>
        {CHART_SKELETON_HEIGHTS.slice(0, 5).map((_, index) => (
          <Skeleton
            key={index}
            variant="text"
            width={48}
            height={14}
            animation="wave"
            sx={{ bgcolor: '#eef1f6' }}
          />
        ))}
      </Box>
    </Box>
  );
}

export default memo(ApiServicePerformanceLoader);
